import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trash2, ArrowUp, ArrowDown, Plus } from 'lucide-react';
import { EntityPicker } from '../EntityPicker';

/**
 * DecisionPolicyWizard — no-code authoring for the intent fine-gate policy.
 *
 * Composes ordered WHEN -> THEN rules (first match wins) and compiles them to the
 * canonical `agentlets.intent` policy whose queryable rule is `allow` returning a
 * decision OBJECT ({allow, effect, workflow_id, approver_role, reason}) — matching
 * IntentRegoMaterializer.INTENT_REGO_QUERY = data.agentlets.intent.allow and the
 * evaluator's object-with-`allow` contract. The full model is embedded as a
 * `# @prove7-wizard-model <base64>` header comment so the Rego round-trips back
 * into the builder; hand-authored Rego (no marker) is left to the Rego tab.
 *
 * Props: value (rego string), onChange(rego), skillNames[], workflows[{id,name}]
 */

const SUBJECTS = [['skill', 'Skill'], ['content', 'Content contains'], ['capability', 'Skill capability'], ['impact', 'Impact (blast radius)'], ['data_class', 'Data class'], ['autonomy', 'Autonomy']];
const CONTENT = [['hasSecret', 'Secret'], ['hasPii', 'PII'], ['hasPci', 'PCI'], ['hasPhi', 'PHI']];
const CAPS = ['read', 'write', 'modify', 'delete', 'revoke', 'send', 'external', 'network', 'destructive'];
const IMPACT = [['gatewayKeys', 'bound keys'], ['delegationChildren', 'child ids'], ['inFlightRuns', 'in-flight runs']];
const DATACLASS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const AUTON = ['HUMAN_CONTROLLED', 'ASSISTED', 'SUPERVISED', 'AUTONOMOUS'];
export const ROLES = ['Compliance', 'Auditor', 'Approver', 'Operator'];
const EFFECTS = [['allow', 'Allow', 'success'], ['hold', 'Require approval', 'warning'], ['route', 'Route to workflow', 'trust'], ['deny', 'Deny', 'danger']];
const uid = () => Math.random().toString(36).slice(2, 8);

const b64e = (o) => { try { return btoa(unescape(encodeURIComponent(JSON.stringify(o)))); } catch (e) { return ''; } };
const b64d = (s) => { try { return JSON.parse(decodeURIComponent(escape(atob(s)))); } catch (e) { return null; } };
// Escape any value interpolated into a quoted Rego string — no policy injection / breakout.
const q = (v) => String(v == null ? '' : v).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

function defaultModel() {
  return {
    rules: [{ id: uid(), conds: [{ subj: 'skill', val: '' }], eff: 'allow', reason: '' }],
    def: { eff: 'hold', role: 'Compliance', mode: 'single', reason: 'Action requires human approval under this intent.' },
  };
}

/* ── compile model -> allow-object Rego ── */
function effObject(o) {
  if (o.eff === 'allow') return '{"allow": true}';
  const eff = o.eff === 'hold' ? 'require_approval' : o.eff === 'route' ? 'route_workflow' : 'deny';
  const L = ['  "allow": false,', '  "effect": "' + eff + '",'];
  if (o.eff === 'route') { L.push('  "workflow_id": "' + q(o.workflow || '') + '",'); L.push('  "approver_role": "' + q(o.role || 'Compliance') + '",'); }
  else if (o.eff === 'hold') {
    L.push('  "approver_role": "' + q(o.role || 'Compliance') + '",');
    if (o.approver) L.push('  "approver": "' + q(o.approver) + '",');
    if (o.mode && o.mode !== 'single') L.push('  "approval_mode": "' + o.mode + '",');
  }
  // Grant-lifecycle — optional per-intent approval validity window (seconds). Authored the same way
  // as the approver; the gate reads it and expires a stale approval past this window. Governed
  // effects only (route_workflow / require_approval). Emitted as a JSON number.
  if ((o.eff === 'route' || o.eff === 'hold') && o.ttl != null && String(o.ttl).trim() !== '') {
    const n = parseInt(o.ttl, 10);
    if (!isNaN(n) && n >= 0) L.push('  "grant_ttl_seconds": ' + n + ',');
  }
  const reason = q(o.reason || eff);
  L.push('  "reason": "' + reason + '",');
  return '{\n' + L.join('\n') + '\n\t}';
}
function condLines(c) {
  if (c.subj === 'skill') {
    if (!c.val) return [];
    const v = String(c.val).trim();
    // Gateway primitives are invoked as `mcp:<name>` at the runtime; match the bare
    // name AND the mcp:-prefixed form so authoring "revoke_key" also fires on
    // "mcp:revoke_key" (the exact mismatch that sent the dry-run to the default).
    if (v.startsWith('mcp:')) return ['input.skill == "' + q(v) + '"'];
    return ['input.skill in {"' + q(v) + '", "mcp:' + q(v) + '"}'];
  }
  if (c.subj === 'impact') { const m = { gt: '>', gte: '>=', lt: '<' }; return ['input.impact.' + (c.field || 'gatewayKeys') + ' ' + (m[c.op || 'gt']) + ' ' + (Number(c.n) || 0)]; }
  if (c.subj === 'data_class') return ['input.data_class == "' + q(c.val || 'RESTRICTED') + '"'];
  if (c.subj === 'autonomy') return ['input.autonomy == "' + q(c.val || 'SUPERVISED') + '"'];
  return [];
}
function ruleHelper(r, idx) {
  const name = 'r' + (idx + 1) + '_match';
  const plain = []; const orBodies = [];
  (r.conds || []).forEach((c) => {
    if (c.subj === 'content') (c.vals || []).forEach((v) => orBodies.push('input.content.' + v + ' == true'));
    else if (c.subj === 'capability') (c.vals || []).forEach((v) => orBodies.push('input.capabilities[_] == "' + q(v) + '"'));
    else plain.push(...condLines(c));
  });
  const bodies = orBodies.length ? orBodies.map((o) => [...plain, o]) : [plain.length ? plain : ['true']];
  return { name, bodies };
}
function compileRego(model) {
  const rules = model.rules || []; const def = model.def || {};
  const helpers = rules.map((r, i) => ruleHelper(r, i));
  const out = [];
  out.push('# This policy was built with the Decision Policy Wizard.');
  out.push('# Edit it visually on the Wizard tab. Rules are checked from top to bottom —');
  out.push('# the first one that matches decides the outcome.');
  out.push('# Keep the next line: it lets the Wizard re-open this policy for editing.');
  out.push('# @prove7-wizard-model ' + b64e(model));
  out.push('package agentlets.intent');
  out.push('import rego.v1');
  out.push('');
  out.push('# Outcome to apply when no rule below matches.');
  out.push('default allow := ' + effObject(def));
  rules.forEach((r, i) => {
    const guards = helpers.slice(0, i).map((h) => 'not ' + h.name);
    const body = [...guards, helpers[i].name].join('\n\t');
    out.push('');
    out.push('# Rule ' + (i + 1));
    out.push('allow := ' + effObject(r) + ' if {\n\t' + body + '\n}');
  });
  out.push('');
  out.push('# Condition checks used by the rules above.');
  helpers.forEach((h) => h.bodies.forEach((b) => out.push(h.name + ' if {\n\t' + b.join('\n\t') + '\n}')));
  return out.join('\n') + '\n';
}
function parseModel(rego) {
  if (!rego) return null;
  const m = rego.match(/# @prove7-wizard-model (\S+)/);
  return m ? b64d(m[1]) : null;
}

const IN = 'v2-focus-ring text-[12.5px] text-v2-ink bg-v2-surface border border-v2-border rounded-v2-sm px-2 py-1';
const IB = 'v2-focus-ring w-6 h-6 flex items-center justify-center rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink-muted hover:text-v2-ink';
const effBorder = { allow: 'border-l-v2-success', hold: 'border-l-v2-warning', route: 'border-l-v2-trust', deny: 'border-l-v2-danger' };
const effOn = {
  allow: 'bg-v2-success-soft text-v2-success', hold: 'bg-v2-warning-soft text-v2-warning',
  route: 'bg-v2-trust-soft text-v2-trust', deny: 'bg-v2-danger-soft text-v2-danger',
};

export function DecisionPolicyWizard({ value, onChange, skillNames = [], workflows = [], users = [], usersByRole = null }) {
  const parsedOnce = useMemo(() => parseModel(value), []); // eslint-disable-line
  const [model, setModel] = useState(() => parsedOnce || defaultModel());
  const [foreign, setForeign] = useState(() => !!(value && value.trim() !== '' && !parsedOnce));
  const lastEmitted = useRef(null);
  const skipEmit = useRef(true); // mount + programmatic syncs must NOT emit; only user edits do
  const mounted = useRef(false);

  // Re-sync from the parent when `value` changes externally (version switch, or edits made in the
  // Rego tab) — but never on mount, and never for our own emission. Prevents stale models AND the
  // zero-regression rule: an untouched draft is never silently overwritten.
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; lastEmitted.current = value; return; }
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    skipEmit.current = true;
    const pm = parseModel(value);
    if (pm) { setForeign(false); setModel(pm); }
    else if (value && value.trim() !== '') { setForeign(true); }
    else { setForeign(false); setModel(defaultModel()); }
  }, [value]); // eslint-disable-line

  // Emit compiled Rego ONLY on genuine user edits (skipped on mount and on programmatic sync).
  useEffect(() => {
    if (foreign) return;
    if (skipEmit.current) { skipEmit.current = false; return; }
    const rego = compileRego(model);
    lastEmitted.current = rego;
    onChange(rego);
  }, [model, foreign]); // eslint-disable-line

  const update = (fn) => setModel((m) => { const c = JSON.parse(JSON.stringify(m)); fn(c); return c; });
  const addRule = () => update((m) => m.rules.push({ id: uid(), conds: [{ subj: 'content', vals: ['hasPii'] }], eff: 'route', workflow: (workflows[0] && workflows[0].id) || '', role: 'Compliance', reason: '' }));
  const delRule = (i) => update((m) => { m.rules.splice(i, 1); });
  const moveRule = (i, d) => update((m) => { const j = i + d; if (j < 0 || j >= m.rules.length) return; const t = m.rules[i]; m.rules[i] = m.rules[j]; m.rules[j] = t; });
  const addCond = (ri) => update((m) => m.rules[ri].conds.push({ subj: 'skill', val: '' }));
  const delCond = (ri, ci) => update((m) => { m.rules[ri].conds.splice(ci, 1); if (!m.rules[ri].conds.length) m.rules[ri].conds.push({ subj: 'skill', val: '' }); });
  const setCond = (ri, ci, k, v) => update((m) => { const c = m.rules[ri].conds[ci]; if (k === 'subj') { Object.keys(c).forEach((x) => x !== 'subj' && delete c[x]); c.subj = v; if (v === 'content' || v === 'capability') c.vals = []; } else c[k] = v; });
  const toggleContent = (ri, ci, val) => update((m) => { const c = m.rules[ri].conds[ci]; c.vals = c.vals || []; c.vals = c.vals.includes(val) ? c.vals.filter((x) => x !== val) : [...c.vals, val]; });
  const setEff = (target, k, v) => update((m) => { const o = target === 'def' ? m.def : m.rules[target]; o[k] = v; if (k === 'eff') { if (v === 'route' && !o.workflow) o.workflow = (workflows[0] && workflows[0].id) || ''; if ((v === 'route' || v === 'hold') && !o.role) o.role = 'Compliance'; } });

  if (foreign) {
    return (
      <div className="border border-v2-warning rounded-v2-md p-4 bg-v2-warning-soft">
        <div className="text-[13px] font-semibold text-v2-ink mb-1">This policy was hand-authored in Rego</div>
        <p className="text-[12px] text-v2-ink-muted mb-3">This policy wasn't built with the Wizard, so it can't be edited here visually. Continue editing it on the <b>Rego</b> tab, or start fresh in the Wizard (this replaces the current policy).</p>
        <button type="button" className="v2-focus-ring text-[12.5px] font-semibold px-3 py-1.5 rounded-v2-sm bg-v2-action text-white" onClick={() => { setForeign(false); setModel(defaultModel()); }}>Start fresh in Wizard</button>
      </div>
    );
  }

  const renderCond = (r, ri, c, ci) => {
    let valueUI = null;
    if (c.subj === 'skill') {
      valueUI = (<><span className="text-[12px] text-v2-ink-muted">is</span>
        <input list="wiz-skill-list" autoComplete="off" spellCheck={false} className={IN} style={{ minWidth: 150 }} value={c.val || ''} placeholder="skill name (e.g. mcp:revoke_key)" onChange={(e) => setCond(ri, ci, 'val', e.target.value)} /></>);
    } else if (c.subj === 'content') {
      valueUI = (<div className="flex gap-1.5 flex-wrap">{CONTENT.map(([k, l]) => (
        <button type="button" key={k} onClick={() => toggleContent(ri, ci, k)} className={'text-[11.5px] rounded-v2-pill px-2.5 py-0.5 border ' + ((c.vals || []).includes(k) ? 'bg-v2-action-soft border-v2-action text-v2-action-strong font-semibold' : 'bg-v2-surface border-v2-border text-v2-ink-muted')}>{l}</button>
      ))}</div>);
    } else if (c.subj === 'capability') {
      valueUI = (<><span className="text-[12px] text-v2-ink-muted">has</span>
        <div className="flex gap-1.5 flex-wrap">{CAPS.map((k) => (
          <button type="button" key={k} onClick={() => toggleContent(ri, ci, k)} className={'text-[11.5px] rounded-v2-pill px-2.5 py-0.5 border ' + ((c.vals || []).includes(k) ? 'bg-v2-action-soft border-v2-action text-v2-action-strong font-semibold' : 'bg-v2-surface border-v2-border text-v2-ink-muted')}>{k}</button>
        ))}</div></>);
    } else if (c.subj === 'impact') {
      valueUI = (<>
        <select className={IN} value={c.field || 'gatewayKeys'} onChange={(e) => setCond(ri, ci, 'field', e.target.value)}>{IMPACT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select className={IN} value={c.op || 'gt'} onChange={(e) => setCond(ri, ci, 'op', e.target.value)}><option value="gt">&gt;</option><option value="gte">&ge;</option><option value="lt">&lt;</option></select>
        <input type="number" className={IN} style={{ width: 64 }} value={c.n == null ? 5 : c.n} onChange={(e) => setCond(ri, ci, 'n', e.target.value)} />
      </>);
    } else if (c.subj === 'data_class') {
      valueUI = (<><span className="text-[12px] text-v2-ink-muted">is</span><select className={IN} value={c.val || 'RESTRICTED'} onChange={(e) => setCond(ri, ci, 'val', e.target.value)}>{DATACLASS.map((v) => <option key={v} value={v}>{v}</option>)}</select></>);
    } else {
      valueUI = (<><span className="text-[12px] text-v2-ink-muted">is</span><select className={IN} value={c.val || 'SUPERVISED'} onChange={(e) => setCond(ri, ci, 'val', e.target.value)}>{AUTON.map((v) => <option key={v} value={v}>{v}</option>)}</select></>);
    }
    return (
      <div key={ci} className="flex gap-1.5 items-center flex-wrap">
        <span className="text-[10px] font-bold text-v2-ink-muted w-7 text-right font-mono-v2">{ci === 0 ? 'IF' : 'AND'}</span>
        <select className={IN + ' font-semibold'} value={c.subj} onChange={(e) => setCond(ri, ci, 'subj', e.target.value)}>{SUBJECTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        {valueUI}
        <span className="flex-1" />
        <button type="button" className={IB} title="Remove condition" onClick={() => delCond(ri, ci)}>&times;</button>
      </div>
    );
  };

  const renderEffect = (o, target) => (
    <div>
      <div className="inline-flex flex-wrap rounded-v2-sm border border-v2-border overflow-hidden bg-v2-surface">
        {EFFECTS.map(([val, label]) => (
          <button type="button" key={val} onClick={() => setEff(target, 'eff', val)}
            className={'text-[12px] font-semibold px-3 py-1.5 border-r border-v2-border-subtle last:border-r-0 ' + (o.eff === val ? effOn[val] : 'text-v2-ink-muted')}>{label}</button>
        ))}
      </div>
      {o.eff === 'route' && (
        <div className="flex gap-2 flex-wrap mt-2 items-end">
          <div className="flex flex-col gap-1" style={{ minWidth: 230 }}>
            <span className="text-[11px] font-semibold text-v2-ink-secondary">Workflow</span>
            <EntityPicker items={workflows} value={o.workflow || null}
              onChange={(id) => setEff(target, 'workflow', id || '')}
              getId={(w) => w.id} getLabel={(w) => w.name}
              ariaLabel="Route-to workflow" placeholder="Select a workflow"
              searchPlaceholder="Search workflows"
              emptyMessage={() => 'No standalone workflows available to route to.'} />
          </div>
          <label className="text-[11px] font-semibold text-v2-ink-secondary flex flex-col gap-1">Approver role
            <select className={IN} value={o.role || 'Compliance'} onChange={(e) => setEff(target, 'role', e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></label>
          <label className="text-[11px] font-semibold text-v2-ink-secondary flex flex-col gap-1">Approval valid for (sec)
            <input type="number" min="0" className={IN} style={{ width: 140 }}
              value={o.ttl == null ? '' : o.ttl}
              onChange={(e) => setEff(target, 'ttl', e.target.value)}
              placeholder="platform default" aria-label="Approval validity window in seconds" /></label>
        </div>
      )}
      {o.eff === 'hold' && (() => {
        // PROVE7-054 — the "specific approver" picker must offer ONLY users who hold the selected
        // approver role, so a non-holder can never be chosen (the runtime would reject them). When
        // usersByRole is supplied the picker is role-filtered; falling back to the full list only if
        // the caller did not wire the role-aware source (keeps older embeds working).
        const roleKey = o.role || 'Compliance';
        const roleUsers = usersByRole ? (usersByRole[roleKey] || []) : users;
        // If the currently-selected approver is not (or no longer) a holder of the selected role,
        // drop it so a stale, invalid approver can't be saved.
        const changeRole = (nr) => {
          setEff(target, 'role', nr);
          const holders = usersByRole ? (usersByRole[nr] || []) : users;
          if (o.approver && !holders.some((h) => h.id === o.approver)) setEff(target, 'approver', '');
        };
        return (
        <div className="flex gap-2 flex-wrap mt-2 items-end">
          <label className="text-[11px] font-semibold text-v2-ink-secondary flex flex-col gap-1">Approver role
            <select className={IN} value={roleKey} onChange={(e) => changeRole(e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></label>
          <div className="flex flex-col gap-1" style={{ minWidth: 230 }}>
            <span className="text-[11px] font-semibold text-v2-ink-secondary">Specific approver <span className="font-normal text-v2-ink-muted">(optional)</span></span>
            <EntityPicker items={roleUsers} value={o.approver || null}
              onChange={(id) => setEff(target, 'approver', id || '')}
              getId={(u) => u.id} getLabel={(u) => u.name}
              allowClear clearLabel={'Any ' + roleKey + ' holder'}
              ariaLabel="Specific approver"
              placeholder={roleUsers.length ? ('Any ' + roleKey + ' holder') : ('No ' + roleKey + ' holders')}
              searchPlaceholder="Search users" />
          </div>
          <label className="text-[11px] font-semibold text-v2-ink-secondary flex flex-col gap-1">Mode
            <select className={IN} value={o.mode || 'single'} onChange={(e) => setEff(target, 'mode', e.target.value)}><option value="single">single</option><option value="dual">dual (four-eyes)</option></select></label>
          <label className="text-[11px] font-semibold text-v2-ink-secondary flex flex-col gap-1">Approval valid for (sec)
            <input type="number" min="0" className={IN} style={{ width: 140 }}
              value={o.ttl == null ? '' : o.ttl}
              onChange={(e) => setEff(target, 'ttl', e.target.value)}
              placeholder="platform default" aria-label="Approval validity window in seconds" /></label>
        </div>
        );
      })()}
      <input className={IN + ' w-full mt-2'} placeholder="Reason (shown to the approver / in the audit)" value={o.reason || ''} onChange={(e) => setEff(target, 'reason', e.target.value)} />
    </div>
  );

  return (
    <div className="flex flex-col gap-2.5">
      <datalist id="wiz-skill-list">{skillNames.map((n) => <option key={n} value={n} />)}</datalist>
      {model.rules.map((r, ri) => (
        <div key={r.id} className={'rounded-v2-md border border-v2-border-subtle bg-v2-surface border-l-2 ' + (effBorder[r.eff] || 'border-l-v2-border')}>
          <div className="flex items-center gap-2 px-3 pt-2.5">
            <span className="w-5 h-5 rounded-v2-sm bg-v2-action text-white text-[11px] font-bold flex items-center justify-center font-mono-v2">{ri + 1}</span>
            <span className="text-[10.5px] font-bold uppercase tracking-wider text-v2-ink-muted">When</span>
            <span className="flex-1" />
            <button type="button" className={IB} title="Move up" onClick={() => moveRule(ri, -1)}><ArrowUp size={13} /></button>
            <button type="button" className={IB} title="Move down" onClick={() => moveRule(ri, 1)}><ArrowDown size={13} /></button>
            <button type="button" className={IB} title="Delete rule" onClick={() => delRule(ri)}><Trash2 size={13} /></button>
          </div>
          <div className="px-3 pb-1 flex flex-col gap-1.5 mt-1.5">{r.conds.map((c, ci) => renderCond(r, ri, c, ci))}</div>
          <button type="button" className="text-[11.5px] text-v2-action font-semibold pl-9 pb-2 pt-0.5 text-left" onClick={() => addCond(ri)}>+ AND condition</button>
          <div className="mx-3 mb-3 mt-1 border-t border-dashed border-v2-border pt-2">
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Then</div>
            {renderEffect(r, ri)}
          </div>
        </div>
      ))}
      <div><button type="button" className="v2-focus-ring text-[12.5px] font-semibold px-3 py-2 rounded-v2-sm border border-v2-action-soft text-v2-action bg-transparent inline-flex items-center gap-1.5" onClick={addRule}><Plus size={13} /> Add rule</button></div>
      <div className="border border-dashed border-v2-border rounded-v2-md p-3 bg-v2-surface">
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Default — when no rule matches</div>
        {renderEffect(model.def, 'def')}
      </div>
      <div className="text-[11.5px] text-v2-ink-muted bg-v2-bg-muted rounded-v2-sm p-2.5" style={{ borderLeft: '2px solid var(--v2-action)' }}>
        Content signals (secret / PII / PCI / PHI), skill capabilities (read / write / delete / …) and impact (blast radius) are detected automatically during a live protected run. The dry-run below lets you test skill, data-class and autonomy conditions now.
      </div>
    </div>
  );
}

export default DecisionPolicyWizard;
